// Private, autosaving notes in a draggable picture-in-picture window, so they can sit beside the
// circle while you play. A player has one pad. A Storyteller has their own pad too, plus a way to
// open any one player's notes read-only — a deliberate act rather than an always-on feed, since the
// server only puts a player's notepad in the projection once it has been asked for by seat.
//
// Notes live for the duration of the game and are dropped when it ends, so nothing here reaches
// the history record.

import { NotebookPen, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn } from "../../lib/ui.js";
import { Button, Textarea } from "../ui/primitives.js";
import { FloatingWindow } from "../ui/FloatingWindow.js";

const SAVE_DEBOUNCE_MS = 600;

/** The button that lives in the side column; the pad itself floats. */
export function NotepadToggle({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpenChange(!open)}
      className="pointer-events-auto flex w-88 items-center justify-between gap-2 rounded-lg border border-ink-line bg-ink-soft/95 px-4 py-2.5 text-left backdrop-blur hover:border-brass/50"
    >
      <span className="text-base font-medium text-parchment">Notepad</span>
      <NotebookPen className={cn("size-5", open ? "text-brass" : "text-parchment-dim")} />
    </button>
  );
}

/** One autosaving textarea. `save` is called at most once per pause in typing. */
export function AutosavePad({
  value,
  placeholder,
  save,
  resetKey,
  className,
}: {
  value: string;
  placeholder: string;
  save: (text: string) => void;
  /** Changes when the pad is about to show something else, so an unsaved draft is not carried over. */
  resetKey: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const dirty = useRef(false);

  useEffect(() => {
    dirty.current = false;
    setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Only adopt the server's copy while there is nothing unsaved locally, so a slow round trip
  // cannot clobber what is being typed right now.
  useEffect(() => {
    if (!dirty.current) setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!dirty.current) return;
    const id = setTimeout(() => {
      save(draft);
      dirty.current = false;
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [draft, save]);

  return (
    <Textarea
      className={className}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => {
        dirty.current = true;
        setDraft(e.target.value);
      }}
    />
  );
}

export function NotepadWindow({ view, onClose }: { view: GameView; onClose: () => void }) {
  const send = useGame((s) => s.send);
  const [tab, setTab] = useState<"mine" | "players">("mine");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = view.seats.find((s) => s.id === selectedId) ?? null;
  // The server only ever holds one opened notepad per Storyteller; ignore it if it is another seat's.
  const viewed =
    view.storyteller?.viewedNotepad?.seatId === selectedId ? view.storyteller.viewedNotepad : null;
  const pick = (seatId: string) => {
    setSelectedId(seatId);
    void send("st:viewNotepad", { seatId });
  };
  const isStoryteller = view.you.isStoryteller;

  const saveMine = (text: string) => void send("player:notepad", { text });
  const saveSeatNote = (text: string) => {
    if (selectedId) void send("st:seatNote", { seatId: selectedId, text });
  };

  return (
    <FloatingWindow title="Notepad" onClose={onClose} storageKey="cc.notepad.pos">
      {isStoryteller && (
        <div className="mb-2 flex gap-1">
          <Button size="sm" variant={tab === "mine" ? "primary" : "secondary"} onClick={() => setTab("mine")}>
            Game Notes
          </Button>
          <Button size="sm" variant={tab === "players" ? "primary" : "secondary"} onClick={() => setTab("players")}>
            Players
          </Button>
        </div>
      )}

      {tab === "mine" || !isStoryteller ? (
        <AutosavePad
          className="min-h-0 flex-1"
          value={view.you.notepad}
          resetKey="mine"
          placeholder={
            isStoryteller
              ? "Your notes on the game as a whole."
              : "Private notes — only you and the storyteller see these."
          }
          save={saveMine}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap gap-1">
            {view.seats.map((seat) => (
              <Button
                key={seat.id}
                size="sm"
                variant={selectedId === seat.id ? "primary" : "secondary"}
                onClick={() => pick(seat.id)}
              >
                {seat.displayName}
              </Button>
            ))}
          </div>
          {selected ? (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto cc-scroll">
              <div className="flex min-h-0 flex-1 flex-col">
                <p className="mb-1 text-xs text-parchment-dim">Your notes on {selected.displayName}</p>
                <AutosavePad
                  className="min-h-16 flex-1"
                  value={view.storyteller?.seatNotes[selected.id] ?? ""}
                  resetKey={selected.id}
                  placeholder="Only the storyteller will see this."
                  save={saveSeatNote}
                />
              </div>
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-1 flex items-center gap-2">
                  <p className="text-xs text-parchment-dim">{selected.displayName}&rsquo;s private notepad (read-only)</p>
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={() => pick(selected.id)}>
                    <RefreshCw className="size-3.5" /> Refresh
                  </Button>
                </div>
                <pre className="min-h-16 flex-1 overflow-y-auto whitespace-pre-wrap rounded border border-ink-line bg-ink p-2 text-sm text-parchment cc-scroll">
                  {viewed ? viewed.text || "— empty —" : "Loading…"}
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-xs text-parchment-dim">Pick a player to view notes.</p>
          )}
        </div>
      )}
    </FloatingWindow>
  );
}
